/**
 * AUTHORED electrical-fixture plan symbols (ELECTRICAL FIXTURES round, 2026-08-12 —
 * docs/MODELER.md "ELECTRICAL FIXTURES family"). A fixture's plan symbol is the
 * DRAFTING STANDARD electrical glyph — duplex circle-with-ticks, S for switch,
 * wall-light glyph for the sconce, the low-voltage triangle — authored parametric
 * 2D, NEVER a projection of the 3D body and never a cut (the objects-round
 * convention, applied harder: a 12" outlet sits below every cut plane and must
 * still print). The symbol is the fixture's ONLY plan representation — no outline
 * prism, no dashed footprint.
 *
 * One implementation, three consumers: the plan cut (plan.ts), the placement ghost
 * (ModelerPage — the ghost IS the real symbol, the doorGhostPts convention), and
 * the type-editor diagram (typePreviewDiagrams.jsx).
 *
 * LOCAL FRAME (fixtureKinds.ts convention): anchor at the fixture's center point ON
 * the mounted wall face — x along the wall centered on 0, y extending off the face
 * INTO the room, feet. `fixtureSymbolLocal` builds in this frame;
 * `fixtureFacePlacement` derives the world anchor + rotation from the host (the
 * caseworkSolid face math — shared by the symbol placer, the 3D instance, and the
 * placement ghost so they can never disagree); `fixturePlanSymbol` composes the two.
 */
import type { ParamValue, Path2D, Pt2, ResolvedElement } from '@aj/bim-kernel'
import {
  centerlineOffsetFt, flattenPathPts, polylinePointAt, isCantedWallParams,
  objectAnchorPoint, isCeilingFixtureKind, isPointFixtureKind, fixtureBodyDims,
  switchGangs, deviceBoxFt, tapeRunOf,
} from '@aj/bim-kernel'
import type { PlanSymbolPath } from '../opening/operationSymbols'
import { placeSymbolPaths } from '../object/planSymbols'

const C = 'fixture.symbol'

function seg(a: Pt2, b: Pt2): PlanSymbolPath {
  return { pts: [a, b], component: C }
}

/** circle as a closed 20-gon (the object-symbol arc convention) */
function circle(cx: number, cy: number, r: number): PlanSymbolPath {
  const pts: Pt2[] = []
  for (let i = 0; i < 20; i++) {
    const a = (Math.PI * 2 * i) / 20
    pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r])
  }
  return { pts, closed: true, component: C }
}

/** axis-aligned rect, closed (CEILING HOSTING round — the square recessed-can trim) */
function rect(x0: number, y0: number, x1: number, y1: number): PlanSymbolPath {
  return { pts: [[x0, y0], [x1, y0], [x1, y1], [x0, y1]], closed: true, component: C }
}

/** arc sample about (cx, cy), radius r, from angle a0 sweeping to a1 (radians) */
function arcPts(cx: number, cy: number, r: number, a0: number, a1: number, segs = 10): Pt2[] {
  const pts: Pt2[] = []
  for (let i = 0; i <= segs; i++) {
    const a = a0 + ((a1 - a0) * i) / segs
    pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r])
  }
  return pts
}

/**
 * The kind's authored plan symbol in the LOCAL face frame. `p` = the resolved TYPE
 * params (fixtureKind) — a synthetic caller (ghost, diagram) can pass a bare type
 * record's params. Sizes are world feet chosen to print at the drafting-standard
 * glyph size on a 1/4" plan (DRAWING INK round, Matt 2026-10-14: the wall-device
 * glyphs were ~2/3 of standard and read as specks — the duplex circle is now 1/8"
 * across at 1/4", the switch S 9/64" tall).
 */
export function fixtureSymbolLocal(p: Record<string, ParamValue>): PlanSymbolPath[] {
  const kind = String(p.fixtureKind || 'outlet')
  // TAPE LIGHTS (2026-11-04): a nominal 2' run for previews (the placed tape draws its real run,
  // tapePlanSymbol)
  if (kind === 'tapeLight') {
    return [
      { pts: [[-1, 0], [1, 0]], dashed: true, component: 'fixture.symbol' },
      { pts: [[-1, -TAPE_TICK_FT / 2], [-1, TAPE_TICK_FT / 2]], component: 'fixture.symbol' },
      { pts: [[1, -TAPE_TICK_FT / 2], [1, TAPE_TICK_FT / 2]], component: 'fixture.symbol' },
    ]
  }
  // CEILING HOSTING round: only the ceiling kinds size off their own type params
  // (a curated 2"/4"/6" trim diameter etc.) — every wall-kind glyph below is a
  // fixed drafting-standard size regardless of body dims, so this is harmless
  // dead weight for those cases.
  const w = (p.widthFt as number) || 2
  const d = (p.depthFt as number) || 2
  const out: PlanSymbolPath[] = []
  switch (kind) {
    case 'switch': {
      // the letter S, authored as two arcs (top bowl CCW over the top, bottom bowl
      // swinging back clockwise) + a short tick tying it to the wall face.
      // GANGED SWITCHES (2026-10-14): one S per gang, side by side
      const r = 0.14
      const cy = 0.16 + 2 * r   // S center sits just off the face
      const g = switchGangs(p)
      const pitch = 0.36        // clears the S's own width (2r) between neighbors
      for (let i = 0; i < g; i++) {
        const x = (i - (g - 1) / 2) * pitch
        // one polyline: top-right, CCW over the top bowl down to the S middle, then
        // CW around the bottom bowl's right side out to the lower-left tail
        const top = arcPts(x, cy + r, r, -Math.PI / 6, (3 * Math.PI) / 2, 12)
        const bottom = arcPts(x, cy - r, r, Math.PI / 2, -(5 * Math.PI) / 6, 12)
        out.push({ pts: [...top, ...bottom.slice(1)], component: C })
        out.push(seg([x, 0], [x, 0.16]))
      }
      // THREE- AND FOUR-WAY SWITCHES (2026-11-03, kernel lighting.ts#switchWays, derived from
      // the pairing): a small 3 or 4 at the lower right of the last S — S3 / S4
      const ways = Number(p.__ways)
      if (ways === 3 || ways === 4) {
        const h = 0.2, w = 0.12
        const x0 = ((g - 1) / 2) * pitch + r + 0.05, y0 = 0.16
        if (ways === 3) {
          const q = h / 4
          const top = arcPts(x0 + w / 2, y0 + 3 * q, q, (5 * Math.PI) / 6, -Math.PI / 2, 8)
          const bottom = arcPts(x0 + w / 2, y0 + q, q, Math.PI / 2, -(5 * Math.PI) / 6, 8)
          out.push({ pts: [...top, ...bottom.slice(1)], component: C })
        } else {
          out.push({ pts: [[x0 + 0.7 * w, y0], [x0 + 0.7 * w, y0 + h], [x0, y0 + 0.35 * h], [x0 + w, y0 + 0.35 * h]], component: C })
        }
      }
      break
    }
    case 'sconce': {
      // wall-light glyph: stem off the face + lamp circle with a center dot read
      const r = 0.22
      out.push(seg([0, 0], [0, 0.12]))
      out.push(circle(0, 0.12 + r, r))
      out.push(circle(0, 0.12 + r, 0.05))
      break
    }
    case 'lowVoltage': {
      // the data/low-voltage triangle, apex into the room
      out.push({ pts: [[-0.21, 0], [0.21, 0], [0, 0.42]], closed: true, component: C })
      break
    }
    // ---- FIT-OUT CATALOG (2026-09-30): sized wall kinds draw their TRUE footprint
    // on the wall face (x along the wall, y into the room) plus a reading mark ----
    case 'grabBar':
    case 'towelBar':
    case 'toiletPaperHolder': {
      const { widthFt: bw, depthFt: bd } = fixtureBodyDims(kind, p)
      out.push(seg([-bw / 2, 0], [-bw / 2, bd]))
      out.push(seg([bw / 2, 0], [bw / 2, bd]))
      out.push(seg([-bw / 2, bd], [bw / 2, bd]))
      break
    }
    case 'towelRing':
    case 'robeHook': {
      const { widthFt: bw, depthFt: bd } = fixtureBodyDims(kind, p)
      out.push(seg([0, 0], [0, bd * 0.5]))
      out.push(circle(0, bd * 0.5 + Math.max(bw, 0.12) / 3, Math.max(bw, 0.12) / 3))
      break
    }
    case 'mirror': {
      // above the cut — drawn dashed
      const { widthFt: bw, depthFt: bd } = fixtureBodyDims(kind, p)
      out.push({ ...rect(-bw / 2, 0, bw / 2, Math.max(bd, 0.06)), dashed: true })
      break
    }
    case 'medicineCabinet': {
      const { widthFt: bw, depthFt: bd } = fixtureBodyDims(kind, p)
      out.push({ ...rect(-bw / 2, 0, bw / 2, bd), dashed: true })
      out.push({ ...seg([-bw / 2, bd], [bw / 2, 0]), dashed: true })
      break
    }
    case 'panel':
    case 'subpanel': {
      // the panelboard convention: the enclosure with its half-filled diagonal
      const { widthFt: bw, depthFt: bd } = fixtureBodyDims(kind, p)
      out.push(rect(-bw / 2, 0, bw / 2, bd))
      out.push({ pts: [[-bw / 2, 0], [bw / 2, 0], [bw / 2, bd]], closed: true, component: C })
      break
    }
    case 'meter':
    case 'evCharger':
    case 'inverter':
    case 'disconnect':
    case 'thermostat': {
      const { widthFt: bw, depthFt: bd } = fixtureBodyDims(kind, p)
      out.push(rect(-bw / 2, 0, bw / 2, bd))
      if (kind === 'meter') out.push(circle(0, bd / 2, Math.min(bw, bd) * 0.35))
      else if (kind === 'disconnect') out.push(seg([-bw / 2, 0], [bw / 2, bd]))
      else if (kind === 'evCharger') out.push(seg([0, 0], [0, bd]))
      else if (kind === 'thermostat') out.push(circle(0, bd + 0.22, 0.2))
      else out.push(seg([-bw / 2, bd], [bw / 2, 0]))
      break
    }
    // ---- CEILING HOSTING round (2026-08-23): ceiling-light glyphs, moved in from
    // the `object` category — the standard electrical-plan conventions, centered
    // on the fixture's own anchor (0,0 — a ceiling light has no wall face to
    // measure off of, unlike every kind above). ----
    case 'recessedCan': {
      // round: circle + inscribed cross (the recessed downlight convention).
      // square: same cross inside a square aperture (the wafer/square-trim convention) —
      // the cross reads as "recessed downlight" either way; only the boundary changes.
      const r = Math.min(w, d) / 2
      if (String(p.trimShape) === 'square') out.push(rect(-r, -r, r, r))
      else out.push(circle(0, 0, r))
      out.push(seg([-r, 0], [r, 0]))
      out.push(seg([0, -r], [0, r]))
      break
    }
    case 'pendant': {
      // circle + center dot + four radial ticks (surface/pendant luminaire convention)
      const r = Math.min(w, d) / 2
      out.push(circle(0, 0, r))
      out.push(circle(0, 0, r * 0.12))
      const t = r * 0.45
      out.push(seg([-r, 0], [-r - t, 0]))
      out.push(seg([r, 0], [r + t, 0]))
      out.push(seg([0, -r], [0, -r - t]))
      out.push(seg([0, r], [0, r + t]))
      break
    }
    case 'flushMount': {
      // concentric circles + diagonal cross (flush/surface-mount convention)
      const r = Math.min(w, d) / 2
      out.push(circle(0, 0, r))
      out.push(circle(0, 0, r * 0.55))
      const k = r * 0.707
      out.push(seg([-k, -k], [k, k]))
      out.push(seg([-k, k], [k, -k]))
      break
    }
    // ---- CEILING DEVICES (docs/bim-plan/28 round 3): the RCP's detectors and registers. The
    // reflected ceiling plan prints each one's code (SD / CO / SR / RR, ceilingTags.js) beside
    // or inside the glyph ----
    case 'smokeDetector':
    case 'coDetector': {
      // a circle with an inner ring — the detector's own disc (its code reads inside it)
      const r = Math.min(w, d) / 2
      out.push(circle(0, 0, r))
      if (kind === 'coDetector') out.push(circle(0, 0, r * 0.82))
      break
    }
    case 'supplyRegister':
    case 'returnRegister': {
      // the grille face to size: supply = crossed (air out every way), return = one diagonal
      out.push(rect(-w / 2, -d / 2, w / 2, d / 2))
      out.push(seg([-w / 2, -d / 2], [w / 2, d / 2]))
      if (kind === 'supplyRegister') out.push(seg([-w / 2, d / 2], [w / 2, -d / 2]))
      break
    }
    // ---- LIGHT STYLES (2026-10-07): the remaining light types. One glyph per TYPE — the
    // instance's style never changes it ----
    case 'chandelier': {
      // the luminaire circle with a ring of candle dots inside it
      const r = Math.min(w, d) / 2
      out.push(circle(0, 0, r))
      out.push(circle(0, 0, r * 0.12))
      for (let k = 0; k < 6; k++) {
        const a = (Math.PI * 2 * k) / 6
        out.push(circle(Math.cos(a) * r * 0.62, Math.sin(a) * r * 0.62, r * 0.09))
      }
      break
    }
    case 'stepLight': {
      // a small rectangle on the face with the half-shaded lower lens (the step-light mark)
      out.push(rect(-0.22, 0, 0.22, 0.14))
      out.push({ pts: [[-0.22, 0.14], [0, 0.3], [0.22, 0.14]], closed: false, component: C })
      break
    }
    case 'postLantern': {
      // the post / pier square with the lamp circle and its cross
      const r = Math.min(w, d) / 2
      out.push(rect(-r, -r, r, r))
      out.push(circle(0, 0, r * 0.62))
      out.push(seg([-r * 0.62, 0], [r * 0.62, 0]))
      out.push(seg([0, -r * 0.62], [0, r * 0.62]))
      break
    }
    case 'pathLight': {
      // a small lamp circle with four short rays (site / path light)
      const r = Math.min(w, d) / 2 * 0.55
      out.push(circle(0, 0, r))
      const k = 0.707
      for (const [sx, sy] of [[1, 1], [-1, 1], [-1, -1], [1, -1]] as [number, number][]) {
        out.push(seg([sx * k * r * 1.15, sy * k * r * 1.15], [sx * k * r * 1.75, sy * k * r * 1.75]))
      }
      break
    }
    default: {
      // duplex receptacle: circle TANGENT to the wall face + the two parallel
      // ticks running from the face through the circle and out the room side (the
      // textbook glyph, kept fully off the face — nothing prints inside the wall)
      const r = 0.25
      const cy = r
      out.push(circle(0, cy, r))
      for (const tx of [-0.095, 0.095]) {
        out.push(seg([tx, 0], [tx, cy + r + 0.1]))
      }
      break
    }
  }
  // KEY STUDS (2026-10-14, Matt: "show an outline of the box in the wall"): a switch,
  // outlet or low-voltage plate sits on an electrical box that reaches back into the wall
  // — its true width (gang count) and depth, drawn from the face inward, so it can be
  // read against the key studs (keyStuds.ts) the plan draws in the same wall
  const box = deviceBoxFt(kind, p)
  if (box) out.push({ ...rect(-box.widthFt / 2, -box.depthFt, box.widthFt / 2, 0), component: 'fixture.box' })
  return out
}

/** minimal host view the face math needs (a full ResolvedElement satisfies it) */
export interface FixtureHostLike {
  params: Record<string, unknown>
  assembly: ResolvedElement['assembly']
  assemblyThicknessFt: number
}

/**
 * The fixture's world FACE PLACEMENT on its host wall: anchor point ON the mounted
 * face at station `s` (arc length along the FLATTENED stored path — fixtures mount
 * on curved walls too, the round's deliberate decision: the device is point-like at
 * wall scale, so station + local tangent is correct on an arc) and the rotation
 * that aims local +y INTO the room on the chosen side (the caseworkSolid
 * convention: sideFlip false = the interior face).
 */
export function fixtureFacePlacement(
  host: FixtureHostLike,
  s: number,
  sideFlip: boolean,
): { pt: Pt2; rotZ: number } | null {
  const hPath = host.params.path as Path2D | null
  if (!hPath || !hPath.nodes || hPath.nodes.length < 2) return null
  const pts = flattenPathPts(hPath)
  const { pt, dir } = polylinePointAt(pts, s)
  const delta = centerlineOffsetFt(
    host.params.locationLine as never, host.params.flipped as never, host.assembly)
  const t = host.assemblyThicknessFt
  const n: Pt2 = [-dir[1], dir[0]]   // left normal
  const flip = sideFlip ? -1 : 1
  const inward = ((host.params.flipped ? 1 : -1) * flip)
  const faceOff = delta + inward * (t / 2)
  const rotZ = Math.atan2(dir[1], dir[0]) + (host.params.flipped ? 0 : Math.PI) + (sideFlip ? Math.PI : 0)
  return { pt: [pt[0] + n[0] * faceOff, pt[1] + n[1] * faceOff], rotZ }
}

/** The element's plan symbol in WORLD coordinates — what plan.ts emits.
 *  `host` may be null for an UNHOSTED ceiling light (2026-08-24 migration
 *  hardening — the symbol places off the element's own anchor, so a missing
 *  ceiling costs nothing here; wall kinds still require their host). */
export function fixturePlanSymbol(el: ResolvedElement, host: ResolvedElement | null, ways?: 3 | 4): PlanSymbolPath[] {
  const kind = String(el.typeParams.fixtureKind || 'outlet')
  // TAPE LIGHTS (2026-11-04): the usual convention — a dashed line along the run, with a short
  // tick across each end so it reads where the tape stops
  if (kind === 'tapeLight') return tapePlanSymbol(el)
  // CEILING HOSTING round (2026-08-23): the ceiling anchor model — the object
  // anchor + rotationDeg, placed directly, no wall-face math (a ceiling has
  // one face, so there's nothing to pick a side of).
  if (isPointFixtureKind(kind)) {
    if (isCeilingFixtureKind(kind) && host && host.category !== 'ceiling') return []
    const anchor = objectAnchorPoint(el)
    if (!anchor) return []
    const rotZ = (((el.params.rotationDeg as number) || 0) * Math.PI) / 180
    return placeSymbolPaths(fixtureSymbolLocal(el.typeParams), anchor, rotZ)
  }
  // CANTED WALLS round (§7 Q6): fixtures refuse canted hosts — mirror of
  // fixtureSolid's guard, so plan and 3D agree the fixture doesn't render
  if (!host || host.category !== 'wall' || isCantedWallParams(host.params)) return []
  const place = fixtureFacePlacement(host, (el.params.hostOffsetFt as number) || 0, el.params.sideFlip === true)
  if (!place) return []
  // a wall fixture's glyph reads its instance too (a switch's gang count)
  // (and a switch's 3-/4-way mark, derived from the whole model by the caller)
  return placeSymbolPaths(fixtureSymbolLocal({ ...el.typeParams, ...el.params, ...(ways ? { __ways: ways } : {}) } as Record<string, ParamValue>), place.pt, place.rotZ)
}

/** TAPE LIGHTS (2026-11-04): a tape light's plan symbol in WORLD coordinates — empty when its
 *  box cannot hold it (no run) */
export const TAPE_TICK_FT = 3 / 12
export function tapePlanSymbol(el: ResolvedElement): PlanSymbolPath[] {
  const run = tapeRunOf(el)
  if (!run) return []
  const { a, b } = run
  const dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy) || 1
  const nx = (-dy / L) * (TAPE_TICK_FT / 2), ny = (dx / L) * (TAPE_TICK_FT / 2)
  return [
    { pts: [a, b], dashed: true, component: 'fixture.symbol' },
    { pts: [[a[0] - nx, a[1] - ny], [a[0] + nx, a[1] + ny]], component: 'fixture.symbol' },
    { pts: [[b[0] - nx, b[1] - ny], [b[0] + nx, b[1] + ny]], component: 'fixture.symbol' },
  ]
}
